// react
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { KeyboardArrowUp as ArrowUpIcon } from '@mui/icons-material';

// material-ui
import { AppBar, Button, Fab, Toolbar, Box, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowIcon from 'components/icons/BridgeArrow';

// third-party

// asset
import siteLogo from 'assets/images/bridge/logo.png';
import { baseColors } from 'utils/constants';
// import { fontWeight, lineHeight, minHeight } from '@mui/system';
type Props = {
  to: string;
  children: React.ReactNode;
};

const ScrollLink = (props: Props) => {
  const { to, children } = props;
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(to);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset;
      const offset = -84;
      window.scrollTo({
        top: y + offset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <a href={`#${to}`} onClick={handleClick}>
      <Typography
        sx={{
          fontSize: '14px',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' }
        }}
      >
        {children}
      </Typography>
    </a>
  );
};

const Header = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMenuToggle = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleMenuClose = () => {
    setMobileMenuOpen(false);
  };

  // スクロール位置を監視
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);

    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);
  // ページトップへのスクロール
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // メニューアイテムの定義//
  const menuItems = [
    { label: '仕組み', type: 'link', to: 'structure' },
    { label: '購入の流れ', type: 'link', to: 'purchase' },
    { label: 'フロー', type: 'link', to: 'flow' },
    { label: '受け取り方法', type: 'link', to: 'acceptance' },
    { label: 'よくある質問', type: 'link', to: 'faq' },
    {
      label: '初回購入はこちら！',
      type: 'button',
      to: '/bridgewifi/ec',
      variant: 'contained',
      sx: {
        backgroundColor: baseColors.bridgeRed,
        color: '#fff',
        '&:hover': {
          backgroundColor: baseColors.bridgeRed
        },
        fontSize: '14px',
        fontWeight: 700,
        minHeight: '60px'
      }
    },
    {
      label: (
        <>
          マイページから
          <br />
          追加チャージ
        </>
      ),
      type: 'button',
      to: '/bridgewifi/ec/mypage',
      variant: 'outlined',
      sx: {
        backgroundColor: baseColors.bridgeBlue,
        color: '#fff',
        '&:hover': {
          backgroundColor: baseColors.bridgeBlue
        },
        fontSize: '14px',
        fontWeight: 700,
        minHeight: '60px',
        lineHeight: '16px'
      }
    }
  ];

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: baseColors.white,
        position: { xs: 'sticky', sm: 'relative' },
        top: { xs: 0, sm: 'auto' },
        zIndex: 1000
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{ margin: '10px auto', width: { xs: '100%', sm: '1200px' }, backgroundColor: baseColors.white }}
      >
        <Toolbar>
          {/* ロゴ画像 */}
          <Box sx={{ flexGrow: 1 }}>
            <img src={siteLogo} alt="logo" style={{ width: '252px' }} />
          </Box>

          {/* メニュー表示 */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 2,
              fontSize: '14px',
              fontWeight: 500,
              color: baseColors.bridgeDGray
            }}
          >
            {menuItems.map((item, idx) => {
              if (item.type === 'button') {
                return (
                  <Button
                    key={idx}
                    variant={item.variant as 'contained' | 'outlined'}
                    sx={item.sx}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = item.to;
                    }}
                  >
                    {item.label}
                  </Button>
                );
              }

              if (item.type === 'link' && item.to) {
                return (
                  <ScrollLink key={idx} to={item.to}>
                    {item.label}
                  </ScrollLink>
                );
              }

              return (
                <Typography key={idx} sx={{ fontSize: '14px', color: 'red' }}>
                  リンク先未設定
                </Typography>
              );
            })}
          </Box>
          {/* スマホ用ハンバーガーボタン */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <IconButton onClick={handleMenuToggle}>{mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}</IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {isVisible && (
        <Fab
          color="primary"
          aria-label="scroll back to top"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16
          }}
        >
          <ArrowUpIcon />
        </Fab>
      )}
      {mobileMenuOpen && (
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1300,
            background: 'linear-gradient(to right, #fff5f5, #f0f9ff)',
            flexDirection: 'column',
            p: 2
          }}
        >
          {/* 上部のロゴ＋クローズボタン */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2
            }}
          >
            <img src={siteLogo} alt="logo" style={{ height: '40px' }} />
            <IconButton onClick={handleMenuClose}>
              <CloseIcon sx={{ fontSize: 32, color: 'red' }} />
            </IconButton>
          </Box>

          {/* メニュー項目 */}
          <Box sx={{ flex: 1 }}>
            {menuItems.map((item, idx) => {
              // ボタンは除外して後でまとめて表示
              if (item.type === 'button') return null;

              return (
                <Box key={idx} sx={{ my: 2, display: 'flex', alignItems: 'center' }}>
                  <ArrowIcon color={baseColors.bridgeRed} width={6} height={12} />
                  {item.type === 'link' && item.to && (
                    <Typography
                      key={idx}
                      component={Link}
                      to={item.to as string}
                      onClick={handleMenuClose}
                      sx={{ fontSize: '16px', fontWeight: 600, paddingLeft: '10px' }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* フッターボタン */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {menuItems
              .filter((item) => item.type === 'button')
              .map((item, idx) => (
                <Button
                  key={idx}
                  variant={item.variant as 'contained' | 'outlined'}
                  sx={{ ...item.sx, flex: 1 }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClose();
                    window.location.href = '/bridgewifi/ec';
                  }}
                >
                  {item.label}
                </Button>
              ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Header;
